import { useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { getACHs } from 'api/ACH';
import { getCards } from 'api/Cards';

import { ia, iaRa } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';

const SCROLL_THRESHOLD = 30;

const CONFIG = {
  card: {
    queryKey: 'cards',
    queryFn: (navigate, params) => getCards(navigate, params),
    formatData: (data) => iaRa(data).filter((card) => !!card?.id),
    defaultActive: 'credit-card',
    savedActive: 'saved-cards'
  },
  ach: {
    queryKey: 'ach',
    queryFn: (navigate, params) => getACHs(navigate, params),
    formatData: (data) => iaRa(data),
    defaultActive: 'new-ach',
    savedActive: 'saved-ach'
  }
};

const useSavedPaymentMethods = ({ patientId, terminalId, type = 'card' }) => {
  const config = CONFIG[type];
  const [active, setActive] = useState(config.defaultActive);
  const scrollRef = useRef(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteFetchData({
    queryKey: config.queryKey,
    queryFn: config.queryFn,
    formatData: config.formatData,
    params: { patientId, terminalId, withCount: true },
    dependencies: [terminalId, patientId],
    options: {
      enabled: !!terminalId && !!patientId
    }
  });

  useEffect(() => {
    if (isLoading) return;
    setActive(ia(data) ? config.savedActive : config.defaultActive);
  }, [data, isLoading, config.defaultActive, config.savedActive]);

  const handleScroll = useCallback(() => {
    const div = scrollRef.current;
    if (!div) return;

    const isNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - SCROLL_THRESHOLD;

    if (isNearBottom && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const div = scrollRef.current;
    if (!div) return;

    const debounced = debounce(handleScroll, 200);
    div.addEventListener('scroll', debounced);

    return () => {
      div.removeEventListener('scroll', debounced);
      debounced.cancel();
    };
  }, [handleScroll, active]);

  const finalData = iaRa(data).filter((item) => !!item.secure_token_id);
  return {
    data: finalData,
    active,
    setActive,
    scrollRef,
    isFetchingNextPage,
    isLoading
  };
};

export default useSavedPaymentMethods;
